import React, { useState } from 'react';
import styled from 'styled-components';
import { getInitials, getColorFromId } from './helper';
import { palette } from 'styled-theme';

// Styled Components
const PanelContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  max-width: 280px;
  max-height: 80%;

  background-color: ${palette('background', 0)};
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  z-index: 10;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 280px;
    right: 8px;
    top: 8px;
  }
`;

const PanelHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
  background-color: ${palette('background', 0)};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HeaderTopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  letter-spacing: -0.3px;
`;

const FriendCount = styled.span`
  background: #e8b471;
  color: #000;
  font-size: 12px;
  font-weight: 600;
  padding: 4px;
  border-radius: 15px;
  min-width: 24px;
  text-align: center;
`;

const ModeSelect = styled.select`
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #e8b471;
  background-color: ${palette('background', 0)};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.2px;
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;

  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23E8B471' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 12px 8px;

  &:focus {
    border-color: #e8b471;
    box-shadow: 0 0 0 2px rgba(232, 180, 113, 0.25);
  }

  option {
    background-color: #222;
    color: #fff;
  }
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    display: none;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: #fff;
  font-size: 13px;
`;

const EmptyIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.6;
`;

const FriendItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid ${(props) => (props.isSelected ? '#e8b471' : 'transparent')};
`;

const FriendAvatar = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 50%;
  border: 2px solid #e8b471;
  overflow: hidden;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: white;
  margin-right: 12px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const FriendInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FriendName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.3px;
`;

const FriendLocation = styled.div`
  font-size: 12px;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #0071e3;
  animation: pulse 1.5s infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
`;

const PanelLoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  text-align: center;
  color: #fff;
  font-size: 13px;
  gap: 12px;
`;

const BigSpinner = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(232, 180, 113, 0.25);
  border-top-color: #e8b471;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const LOCATION_MODE = {
  FRIENDS: 'FRIENDS',
  BUSINESS: 'BUSINESS',
};

// Main Component
const FriendLocationPanel = ({
  friendsData = [],
  selectedFriend = null,
  onFriendSelect = () => {},
  loadingFriendId = null,
  locationMode = LOCATION_MODE.FRIENDS,
  onModeChange = () => {},
  isLoading = false,
}) => {
  const handleModeChange = (e) => {
    onModeChange(e.target.value);
  };

  const panelTitle =
    locationMode === LOCATION_MODE.BUSINESS ? 'Business Location' : 'Friends Location';

  const headerBlock = (count) => (
    <PanelHeader>
      <HeaderTopRow>
        <HeaderTitle>{panelTitle}</HeaderTitle>
        <FriendCount>{count}</FriendCount>
      </HeaderTopRow>
      <ModeSelect value={locationMode} onChange={handleModeChange}>
        <option value={LOCATION_MODE.FRIENDS}>Friends</option>
        <option value={LOCATION_MODE.BUSINESS}>Business</option>
      </ModeSelect>
    </PanelHeader>
  );

  // Loading state - data fetch for the current mode is in flight
  if (isLoading) {
    return (
      <PanelContainer>
        {headerBlock(0)}
        <PanelContent>
          <PanelLoadingState>
            <BigSpinner />
            <div>
              {locationMode === LOCATION_MODE.BUSINESS
                ? 'Loading business locations...'
                : 'Loading friend locations...'}
            </div>
          </PanelLoadingState>
        </PanelContent>
      </PanelContainer>
    );
  }

  if (!Array.isArray(friendsData) || friendsData.length === 0) {
    return (
      <PanelContainer>
        {headerBlock(0)}
        <PanelContent>
          <EmptyState>
            <EmptyIcon>📍</EmptyIcon>
            <div>
              {locationMode === LOCATION_MODE.BUSINESS
                ? 'No business locations sharing location'
                : 'No friends sharing location'}
            </div>
          </EmptyState>
        </PanelContent>
      </PanelContainer>
    );
  }

  const handleFriendClick = (friend) => {
    onFriendSelect(friend);
  };

  const isSelected = (friendId) => selectedFriend?._id === friendId;

  return (
    <PanelContainer>
      {headerBlock(friendsData.length)}
      <PanelContent>
        {friendsData.map((friend) => {
          const hasProfileImg = friend?.userInfo?.profileImg;
          const initials = getInitials(friend?.userInfo?.firstName, friend?.userInfo?.lastName);
          const bgColor = getColorFromId(friend._id);
          const isLoadingThis = loadingFriendId === friend._id;

          return (
            <FriendItem
              key={friend._id}
              onClick={() => handleFriendClick(friend)}
              isSelected={isSelected(friend._id)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleFriendClick(friend);
                }
              }}
            >
              <FriendAvatar bgColor={bgColor} isSelected={isSelected(friend._id)}>
                {hasProfileImg ? (
                  <img
                    src={friend.userInfo.profileImg}
                    alt={`${friend.userInfo.firstName} ${friend.userInfo.lastName}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  initials
                )}
              </FriendAvatar>
              <FriendInfo>
                <FriendName>
                  {friend.userInfo.firstName} {friend.userInfo.lastName}
                </FriendName>
                {isLoadingThis ? (
                  <FriendLocation>
                    <LoadingSpinner /> Loading location...
                  </FriendLocation>
                ) : (
                  <FriendLocation>
                    {friend.location.displayName || 'Location loaded'}
                  </FriendLocation>
                )}
                <FriendLocation>
                  {friend?.updatedAt &&
                    new Date(friend?.updatedAt).toLocaleString('en-US', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      hour12: true,
                    })}
                </FriendLocation>
              </FriendInfo>
            </FriendItem>
          );
        })}
      </PanelContent>
    </PanelContainer>
  );
};

export default FriendLocationPanel;
